import { useEffect, useState, useCallback, useRef } from "react";
import { Search, ChevronDown, ChevronUp, Globe } from "lucide-react";
import { RedirectListSkeleton } from "../../components/SkeletonLoading/PageSkeletons";
import { authService } from "../../services/api";
import { useNotification } from "../../components/Notifications/NotificationContext";
import { useAuth } from "../../context/AuthProvider.jsx";
import { getVisibleBots } from "../../utils/bots";
import RedirectForm from "./RedirectForm";
import RedirectList from "./RedirectList";
import StatsModal from "./StatsModal";
import CampaignModal from "./CampaignModal";
import DomainsModal from "./DomainsModal";
import DomainMigrationNotice from "./DomainMigrationNotice";
import InterstitialModal from "./interstitial/InterstitialModal";

const FILTERS = [
    { id: "all", label: "Todos" },
    { id: "cloaker", label: "Cloaker" },
];

export default function Redirecionadores() {
    const { user } = useAuth();
    const bots = getVisibleBots(user?.bots);
    const { showError, showSuccess } = useNotification();

    const [secondaryApiUrl, setSecondaryApiUrl] = useState("");

    const [loading, setLoading] = useState(true);
    const [redirects, setRedirects] = useState([]);
    const [metricsMap, setMetricsMap] = useState({});
    // Métricas só sob demanda ("Ver métricas"): a lista abre sem esperar a
    // agregação de cliques/vendas, que é a parte cara.
    const [showMetrics, setShowMetrics] = useState(false);
    const [metricsLoading, setMetricsLoading] = useState(false);
    const showMetricsRef = useRef(false);
    const metricsReqRef = useRef(0);

    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState("all");

    const [showForm, setShowForm] = useState(false);
    const [editingRedirect, setEditingRedirect] = useState(null);

    const [statsRedirect, setStatsRedirect] = useState(null);
    const [campaignRedirect, setCampaignRedirect] = useState(null);
    const [interstitialRedirect, setInterstitialRedirect] = useState(null);
    const [domainsOpen, setDomainsOpen] = useState(false);
    // Domínios disponíveis: só para o aviso de migração no topo (1 GET leve).
    const [availableDomains, setAvailableDomains] = useState([]);

    const loadDomains = useCallback(() => {
        authService.redirects.list_domains()
            .then((res) => setAvailableDomains(res.data?.data?.available || []))
            .catch(() => {});
    }, []);
    useEffect(() => { loadDomains(); }, [loadDomains]);

    const reqIdRef = useRef(0);

    /* ===================== Métricas (sob demanda) ===================== */
    const loadMetrics = useCallback(async (list) => {
        const myReq = ++metricsReqRef.current;
        const slugs = (list || []).map((r) => r.slug);
        if (!slugs.length) { setMetricsMap({}); return; }
        setMetricsLoading(true);
        try {
            // Todos os redirects numa única chamada (evita N+1)
            const mres = await authService.redirects.get_metrics_batch(slugs);
            if (myReq !== metricsReqRef.current) return;
            setMetricsMap(mres.data?.success ? (mres.data.data || {}) : {});
        } catch {
            if (myReq === metricsReqRef.current) setMetricsMap({});
        } finally {
            if (myReq === metricsReqRef.current) setMetricsLoading(false);
        }
    }, []);

    /* ===================== Load redirects ===================== */
    const loadRedirects = useCallback(async (q, f) => {
        const myReq = ++reqIdRef.current;
        try {
            setLoading(true);
            const res = await authService.redirects.list_redirects({ q: q || undefined, filter: f });
            if (myReq !== reqIdRef.current) return; // resposta obsoleta
            if (!res.data?.success) return;

            const list = res.data.data.redirects || [];
            setRedirects(list);
            setSecondaryApiUrl(res.data.data.secondary_api_url || "");
            setMetricsMap({});
            // Com as métricas abertas, atualiza para a nova lista sem travar a tela.
            if (showMetricsRef.current) loadMetrics(list);
        } catch (err) {
            console.error("Erro ao carregar redirecionadores", err);
        } finally {
            if (myReq === reqIdRef.current) setLoading(false);
        }
    }, [loadMetrics]);

    const toggleMetrics = () => {
        const next = !showMetrics;
        setShowMetrics(next);
        showMetricsRef.current = next;
        if (next) loadMetrics(redirects);
    };

    // Recarrega quando busca (debounced) ou filtro mudam
    useEffect(() => {
        const t = setTimeout(() => loadRedirects(search, filter), 300);
        return () => clearTimeout(t);
    }, [search, filter, loadRedirects]);

    /* ===================== CRUD ===================== */
    // Salva a resposta ao tráfego bloqueado. Para redirect, o backend confere se a
    // URL responde; se não, devolve o motivo e nada é alterado.
    async function saveBlockResponse(slug, formData) {
        if (!formData.hasCloaker) return null;
        try {
            await authService.redirects.update_block_response(slug, {
                block_method: formData.blockMethod,
                safe_url: formData.blockMethod === "redirect" ? formData.safeUrl.trim() : null,
            });
            return null;
        } catch (err) {
            return err?.response?.data?.message || "Não foi possível validar a URL segura.";
        }
    }

    async function createRedirect(formData) {
        try {
            const res = await authService.redirects.create_redirect({
                slug: formData.slug,
                bot_ids: formData.selectedBots,
                routing_mode: formData.routingMode,
                has_cloaker: formData.hasCloaker,
            });
            if (formData.hasCloaker && formData.blockMethod === "redirect") {
                const blockErr = await saveBlockResponse(formData.slug, formData);
                if (blockErr) {
                    // Criado com a página segura padrão; mantém o form aberto em edição p/ corrigir a URL.
                    showError(`Redirecionador criado, mas a URL segura não foi salva: ${blockErr}`);
                    setEditingRedirect({ ...(res.data?.data || {}), slug: formData.slug, has_cloaker: true, block_method: "redirect", safe_url: formData.safeUrl });
                    loadRedirects(search, filter);
                    return;
                }
            }
            showSuccess("Redirecionador criado com sucesso.");
            setShowForm(false);
            loadRedirects(search, filter);
        } catch (err) {
            console.error("Erro ao criar redirecionador", err);
            showError("Erro ao criar redirecionador. Verifique se o slug é único e tente novamente.");
        }
    }

    async function updateRedirect(formData) {
        if (!editingRedirect) return;
        // URL segura primeiro: se não responder, o redirecionador não é atualizado.
        const blockErr = await saveBlockResponse(editingRedirect.slug, formData);
        if (blockErr) {
            showError(blockErr);
            return;
        }
        try {
            await authService.redirects.update_redirect(editingRedirect.slug, {
                bot_ids: formData.selectedBots,
                routing_mode: formData.routingMode,
                has_cloaker: formData.hasCloaker,
            });
            showSuccess("Redirecionador atualizado com sucesso.");
            setShowForm(false);
            setEditingRedirect(null);
            loadRedirects(search, filter);
        } catch (err) {
            console.error("Erro ao atualizar redirecionador", err);
            showError("Erro ao atualizar redirecionador.");
        }
    }

    async function deleteRedirect(slug) {
        if (!window.confirm(`Excluir o redirecionador "${slug}"?`)) return;
        try {
            await authService.redirects.delete_redirect(slug);
            showSuccess("Redirecionador excluído com sucesso.");
            loadRedirects(search, filter);
        } catch (err) {
            console.error("Erro ao excluir redirecionador", err);
            showError("Erro ao excluir redirecionador.");
        }
    }

    function editRedirect(redirect) {
        setEditingRedirect(redirect);
        setShowForm(true);
    }

    /* ===================== Render ===================== */
    return (
        <div className="page">
            {/* Notícia no topo: domínio da Spectrum que vai expirar */}
            <DomainMigrationNotice domains={availableDomains} />
            <header className="mb-6 flex justify-between flex-wrap flex-row gap-4">
                <div>
                    <h1 className="title">Redirecionadores</h1>
                    <p className="text-gray-400">
                        Crie slugs que redirecionam para seus bots e marcam vendas.
                    </p>
                </div>
                <div className="flex items-center gap-2 self-start mt-4 flex-wrap">
                    <button
                        type="button"
                        onClick={toggleMetrics}
                        className="inline-flex items-center justify-center gap-2 py-2 px-4 rounded-xl border border-dashed border-white/[0.08] text-white/60 text-sm hover:text-white hover:border-white/20 transition-colors"
                    >
                        {showMetrics ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        {showMetrics ? "Ocultar métricas" : "Ver métricas"}
                    </button>
                    {/* Domínios e Criar Link sempre lado a lado, com a mesma altura */}
                    <div className="flex items-stretch gap-2">
                        <button
                            type="button"
                            onClick={() => setDomainsOpen(true)}
                            className="btn-secondary inline-flex items-center justify-center gap-2 text-xs md:text-base"
                        >
                            <Globe size={16} /> Domínios
                        </button>
                        <button onClick={() => { setEditingRedirect(null); setShowForm(true); }} className="btn-primary text-xs md:text-base">
                            <i className="fas fa-plus"></i> Criar Link
                        </button>
                    </div>
                </div>
            </header>

            {/* Toolbar: busca + filtros */}
            <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-3 md:p-4 mb-4 flex flex-col md:flex-row md:items-center gap-3">
                <div className="relative flex-1 min-w-0">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Buscar por slug ou URL"
                        className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.08] text-sm text-white placeholder-white/40 outline-none focus:border-[rgba(139,92,246,0.4)]"
                    />
                </div>
                <div className="sx-filterbar">
                    {FILTERS.map((f) => (
                        <button
                            key={f.id}
                            className={`sx-filter-chip ${filter === f.id ? "is-active" : ""}`}
                            onClick={() => setFilter(f.id)}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* <div className="text-xs text-white/40 mb-2">
                {redirects.length} {redirects.length === 1 ? "link" : "links"}
            </div> */}

            {loading ? (
                <RedirectListSkeleton />
            ) : (
                <div className="spt-reveal">
                <RedirectList
                    redirects={redirects}
                    bots={bots}
                    metricsMap={metricsMap}
                    showMetrics={showMetrics}
                    metricsLoading={metricsLoading}
                    secondaryApiUrl={secondaryApiUrl}
                    onDelete={deleteRedirect}
                    onEdit={editRedirect}
                    onCreateNew={() => { setEditingRedirect(null); setShowForm(true); }}
                    onOpenStats={(r) => setStatsRedirect(r)}
                    onOpenCampaign={(r) => setCampaignRedirect(r)}
                    onOpenInterstitial={(r) => setInterstitialRedirect(r)}
                />
                </div>
            )}

            {/* Modais */}
            <RedirectForm
                open={showForm}
                bots={bots}
                onSubmit={editingRedirect ? updateRedirect : createRedirect}
                onCancel={() => { setShowForm(false); setEditingRedirect(null); }}
                initialData={editingRedirect}
                isEditing={!!editingRedirect}
                ownBaseUrl={secondaryApiUrl}
            />
            <StatsModal
                open={!!statsRedirect}
                slug={statsRedirect?.slug}
                siteUrl={statsRedirect ? `${secondaryApiUrl}/r/${statsRedirect.slug}` : ""}
                onClose={() => setStatsRedirect(null)}
            />
            <CampaignModal
                open={!!campaignRedirect}
                slug={campaignRedirect?.slug}
                onClose={() => setCampaignRedirect(null)}
            />
            <DomainsModal open={domainsOpen} onClose={() => setDomainsOpen(false)} onChange={loadDomains} />
            <InterstitialModal
                open={!!interstitialRedirect}
                redirect={interstitialRedirect}
                bots={bots}
                onClose={() => setInterstitialRedirect(null)}
            />
        </div>
    );
}
